import { useState, useEffect, useMemo, useCallback, ChangeEvent } from 'react';
import { Attestation, AttestationStatus, FilterState } from '../../types/attestation';
import { GestorService, IAttestationFilters } from '../../services/Gestor';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { useParams } from 'react-router-dom';
import {
  IAttestationAPI,
  IAttestationResponse,
  DateFilterOption,
  StatusFilterOption,
  TodayFilterOption,
  STATUS_VALUES_MAP,
} from './types';
import { useAuth } from '../../context/Auth';


export { DATE_OPTIONS, STATUS_OPTIONS, TODAY_OPTIONS } from './types';

// Map API type to frontend type
const mapType = (tipo: string): string => {
  switch (tipo) {
    case 'D':
      return 'Atestado médico (dias)';
    case 'H':
      return 'Atestado médico (horas)';
    default:
      return 'Atestado médico';
  }
};

// Map API response to Attestation type
const isSentinel1900Date = (dateString?: string | null) =>
  Boolean(dateString) && (dateString ?? '').startsWith('1900-01-01');

const mapApiToAttestation = (apiData: IAttestationAPI): Attestation => ({
  id: String(apiData.Id),
  employeeName: apiData.Nome || apiData.UserName || 'Nome não informado',
  employeeCpf: apiData.Matricula || '',
  employeeRegistration: apiData.Matricula || '',
  employeeEmail: apiData.UserMail || '',
  submissionDate: apiData.DataEntrega || '',
  type: mapType(apiData.Tipo),
  tipo: apiData.Tipo || null,
  startDate:
    isSentinel1900Date(apiData.DeDiaHora) && apiData.DataAtestado
      ? apiData.DataAtestado
      : apiData.DeDiaHora || apiData.DataAtestado || '',
  endDate:
    isSentinel1900Date(apiData.AteDiaHora) && apiData.DataAtestado
      ? apiData.DataAtestado
      : apiData.AteDiaHora || apiData.DataAtestado || '',
  Status: Number(apiData.Status) as AttestationStatus,
  OBS: apiData.OBS || '',
  Motivo: apiData.Motivo || '',
  MotivoDescricao: apiData.MotivoDescricao || null,
  SubMotivoDescricao: apiData.SubMotivoDescricao || null,
});

export const useHome = () => {
  const [allAttestations, setAllAttestations] = useState<Attestation[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // const [tokenAuth] = useLocalStorage<string>({ storageKey: '@token_nhg' })
  const { tokenAuth } = useAuth()
  const { entidadeId, instanciaId, vidaId } = useParams()

  const [hasTeam] = useState<boolean>(true);
  const [filters, setFilters] = useState<FilterState>({
    status: [],
    dateRange: null,
    searchText: '',
  });
  const [todayFilter, setTodayFilter] = useState<TodayFilterOption | null>(null);

  const isWithinPeriod = (startDate: string, endDate: string): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(0, 0, 0, 0);
    return today >= start && today <= end;
  };

  const getAttestations = useCallback(async () => {
    if (!entidadeId || !instanciaId || !vidaId) return

    const requestFilters: IAttestationFilters = {
      InstanciaId: Number(instanciaId),
      EntidadeId: Number(entidadeId),
      GestorCpf: vidaId,
    }

    setIsLoading(true)
    try {
      const result: IAttestationResponse = await GestorService.getAttestations(requestFilters)
      if (result?.Success && result.Data) {
        setAllAttestations(result.Data.map(mapApiToAttestation))
      }
    } catch (error) {
      console.error('[useHome] Error fetching attestations:', error)
    } finally {
      setIsLoading(false)
    }
  }, [entidadeId, instanciaId, vidaId]);

  useEffect(() => {
    if (tokenAuth) {
      getAttestations()
    } else {
      setIsLoading(true)
    }
  }, [tokenAuth]);

  // Helper: get date range boundaries
  const getDateBoundaries = (range: string | null) => {
    if (!range) return null;

    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const todayEnd = new Date(todayStart.getTime() + 24 * 60 * 60 * 1000 - 1);

    switch (range) {
      case 'Hoje':
        return { start: todayStart, end: todayEnd };
      case 'Última semana': {
        const weekAgo = new Date(todayStart);
        weekAgo.setDate(weekAgo.getDate() - 7);
        return { start: weekAgo, end: todayEnd };
      }
      case 'Últimos 30 dias': {
        const monthAgo = new Date(todayStart);
        monthAgo.setDate(monthAgo.getDate() - 30);
        return { start: monthAgo, end: todayEnd };
      }
      default:
        return null;
    }
  };

  const filteredAttestations = useMemo(() => {
    const dateBounds = getDateBoundaries(filters.dateRange);
    const searchLower = filters.searchText.toLowerCase();

    return allAttestations
      .filter((att) => {
        if (filters.status.length > 0 && !filters.status.includes(att.Status)) return false;

        if (dateBounds) {
          const startDate = new Date(att.startDate);
          if (isNaN(startDate.getTime())) return false;
          if (startDate < dateBounds.start || startDate > dateBounds.end) return false;
        }

        if (searchLower) {
          const match =
            att.employeeName.toLowerCase().includes(searchLower) ||
            att.employeeCpf.includes(searchLower) ||
            att.employeeRegistration.includes(searchLower) ||
            att.employeeEmail.toLowerCase().includes(searchLower);
          if (!match) return false;
        }

        if (todayFilter === 'Sim' && !isWithinPeriod(att.startDate, att.endDate)) return false;
        if (todayFilter === 'Não' && isWithinPeriod(att.startDate, att.endDate)) return false;
        if (todayFilter === null) { /* no filter applied */ }

        return true;
      })
      .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
  }, [allAttestations, filters.dateRange, filters.searchText, filters.status, todayFilter]);

  const handleDateFilter = useCallback((option: DateFilterOption) => {
    setFilters((prev) => ({
      ...prev,
      dateRange: prev.dateRange === option ? null : option,
    }));
  }, []);

  const handleStatusFilter = useCallback((option: StatusFilterOption) => {
    setFilters((prev) => {
      const currentStatus = STATUS_VALUES_MAP[option];
      const isSameFilter = JSON.stringify(prev.status) === JSON.stringify(currentStatus);
      return {
        ...prev,
        status: isSameFilter ? [] : currentStatus,
      };
    });
  }, []);

  const handleSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setFilters((prev) => ({ ...prev, searchText: e.target.value }));
  }, []);

  const handleTodayFilter = useCallback((option: TodayFilterOption) => {
    setTodayFilter((prev) => (prev === option ? null : option));
  }, []);

  const clearAllFilters = useCallback(() => {
    setFilters({ status: [], dateRange: null, searchText: '' });
    setTodayFilter(null);
  }, []);

  const activeStatusOption = useMemo((): StatusFilterOption | null => {
    if (filters.status.length === 0) return null;
    if (filters.status.includes(5) || filters.status.includes(7)) return 'Aprovados';
    if (filters.status.includes(6)) return 'Reprovados';
    if (filters.status.includes(3)) return 'Em Análise';
    return null;
  }, [filters.status]);

  const activeDateOption = useMemo((): DateFilterOption | null => {
    return filters.dateRange as DateFilterOption | null;
  }, [filters.dateRange]);

  const statistics = useMemo(() => {
    const approved = filteredAttestations.filter(
      (att) => att.Status === 5 || att.Status === 7
    ).length;
    const rejected = filteredAttestations.filter(
      (att) => att.Status === 6
    ).length;
    const inAnalysis = filteredAttestations.filter(
      (att) => att.Status === 3
    ).length;

    const now = new Date();
    const employeesOnLeave = new Set(
      filteredAttestations
        .filter((att) => {
          const start = new Date(att.startDate);
          const end = new Date(att.endDate);
          return now >= start && now <= end;
        })
        .map((att) => att.employeeCpf)
    ).size;

    return { approved, rejected, inAnalysis, employeesOnLeave };
  }, [filteredAttestations]);

  return {
    hasTeam,
    isLoading,
    filteredAttestations,
    filters,
    statistics,
    activeStatusOption,
    activeDateOption,
    todayFilter,
    handleDateFilter,
    handleStatusFilter,
    handleSearchChange,
    handleTodayFilter,
    clearAllFilters,
  };
};
